import { createElement as rc, Fragment, useContext, useEffect, useState } from 'react';
import lodash from 'lodash';
import { http } from 'lib_ui-services';
import { contexts } from 'lib_ui-primitives';

/** Resolve a Backbone API path within the viewer's current service group.
 * Only {recordId} is substituted; URLs never contain executable metadata.
 */
export function resolveApiPath(path, recordId, groupNumber = 0) {
    // Metadata uses literal paths; reject encoded segments before encoding the record ID.
    if (typeof path !== 'string' || !path.startsWith('/api/') || /[\\%?#]/.test(path))
        throw new Error('Use a Backbone API path beginning with /api/.');
    const resolved = path.replaceAll('{recordId}', encodeURIComponent(String(recordId)));
    if (/[{}]/.test(resolved) || resolved.split('/').includes('..'))
        throw new Error('The API path supports only the {recordId} placeholder.');
    return `/g/${groupNumber}${resolved}`;
}

/** Load temporary records without a database view. Requests run sequentially;
 * changing the record, tenant or tab discards old responses and stops refreshing.
 * A zero refresh interval loads once.
 */
export default function ApiSource({ hNode, record, visible = true, api = http, children }) {
    const session = useContext(contexts.SessionContext) || {};
    const group = session.groupNumber || 0;
    const tenant = session.tenant?.['identity:tenant']?._id;
    const { apiPath, recordsProperty = 'records', refreshSeconds = 0 } = hNode;
    const scope = JSON.stringify([group, tenant, record._id, apiPath, recordsProperty, refreshSeconds]);
    const [state, setState] = useState(null);
    useEffect(() => {
        if (!visible) return;
        const current = { active: true, pending: null, timer: null };
        setState(null);
        async function load() {
            try {
                const path = resolveApiPath(apiPath, record._id, group);
                const data = await api.get(path);
                const records = Array.isArray(data) ? data : lodash.get(data, recordsProperty);
                if (!Array.isArray(records) || records.some(row => !row || row._id == null))
                    throw new Error('The API must return records with a stable _id. Check the records property.');
                if (current.active) setState({ scope, data, records, viewReady: true, error: '' });
            } catch (error) {
                if (current.active)
                    setState(previous => ({
                        ...(previous?.scope === scope ? previous : { records: [] }),
                        scope,
                        viewReady: true,
                        error: error.message || 'Unable to load records'
                    }));
            }
        }
        current.refresh = () => {
            if (!current.active) return;
            if (current.pending) return current.pending;
            clearTimeout(current.timer);
            current.pending = load().finally(() => {
                current.pending = null;
                const seconds = Number(refreshSeconds);
                if (current.active && Number.isFinite(seconds) && seconds > 0)
                    current.timer = setTimeout(current.refresh, Math.max(1, seconds) * 1000);
            });
            return current.pending;
        };
        current.refresh();
        return () => {
            current.active = false;
            clearTimeout(current.timer);
        };
    }, [api, scope, apiPath, record._id, group, recordsProperty, refreshSeconds, visible]);

    const current = state?.scope === scope ? state : null;
    const data = current?.data;
    const status = [
        typeof data?.status === 'string' && data.status,
        data?.updatedAt && new Date(data.updatedAt).toLocaleString()
    ]
        .filter(Boolean)
        .join(' · ');
    return rc(Fragment, { key: scope }, children({
        records: current?.records || [],
        viewReady: !!current?.viewReady,
        status,
        error: current?.error || ''
    }));
}
